import { useState, type Dispatch, type SetStateAction } from 'react';

import { Button } from '@/shared/components/shadcn/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';

import {
  getScrapingIntentKind,
  type ScrapingIntentKind,
  toScrapingIntentViewModel,
} from '../../model/scraping-intent-view-model';
import { SCRAPING_INTENT_MODE, type ScrapingIntentSummary } from '../../types';
import { ScrapingStatusBadge } from '../../ui/scraping-status-badge';
import { ScrapingIntentDetailModal } from './scraping-intent-detail-modal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@syncly/ui';
import { FilterIcon } from 'lucide-react';

type ScrapingIntentTableProps = {
  intents: ScrapingIntentSummary[];
  kind: ScrapingIntentKind;
  mode: SCRAPING_INTENT_MODE | undefined;
  setMode: Dispatch<SetStateAction<SCRAPING_INTENT_MODE | undefined>>;
};

export function ScrapingIntentTable({ intents, kind, mode, setMode }: ScrapingIntentTableProps) {
  const [selectedIntent, setSelectedIntent] = useState<ScrapingIntentSummary | null>(null);
  const filteredIntents = intents.filter((intent) => getScrapingIntentKind(intent) === kind);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow gridTemplateColumns="minmax(220px,2fr) minmax(110px,1fr) minmax(110px,1fr) minmax(140px,1fr) minmax(160px,1fr) minmax(320px,2fr)">
            <TableHead>Target</TableHead>
            <TableHead>Platform</TableHead>
            <TableHead>
              <div className="flex items-center gap-1">
                Mode
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-4 w-4 p-0">
                      <FilterIcon className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem onClick={() => setMode(undefined)}>
                      All {mode === undefined && '✓'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setMode(SCRAPING_INTENT_MODE.ONETIME)}>
                      One-time {mode === SCRAPING_INTENT_MODE.ONETIME && '✓'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setMode(SCRAPING_INTENT_MODE.PERIODIC)}>
                      Periodic {mode === SCRAPING_INTENT_MODE.PERIODIC && '✓'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Latest run</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredIntents.map((intent) => {
            const viewModel = toScrapingIntentViewModel(intent);

            return (
              <TableRow
                key={intent.intent_id}
                gridTemplateColumns="minmax(220px,2fr) minmax(110px,1fr) minmax(110px,1fr) minmax(140px,1fr) minmax(160px,1fr) minmax(320px,2fr)"
                className="cursor-pointer"
                onClick={() => setSelectedIntent(intent)}
              >
                <TableCell className="min-w-0">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate font-medium">{viewModel.target}</span>
                    <span className="text-muted-foreground truncate text-xs">
                      {viewModel.targetDescription || intent.intent_id}
                    </span>
                  </div>
                </TableCell>
                <TableCell>{viewModel.platform}</TableCell>
                <TableCell>{viewModel.mode}</TableCell>
                <TableCell>
                  <ScrapingStatusBadge label={viewModel.status} tone={viewModel.statusTone} />
                </TableCell>
                <TableCell>
                  {viewModel.latestRun == null ? (
                    '-'
                  ) : (
                    <ScrapingStatusBadge
                      label={viewModel.latestRun.statusLabel}
                      tone={viewModel.latestRun.tone}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSelectedIntent(intent);
                      }}
                    >
                      Details
                    </Button>
                    {/* <ScrapingIntentActions intent={intent} /> */}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ScrapingIntentDetailModal
        intent={selectedIntent}
        open={selectedIntent != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedIntent(null);
          }
        }}
      />
    </>
  );
}
